import React, { useRef, useState } from 'react';
import { ArrowRight, Check, CheckCircle2, FileSpreadsheet, Info, Loader2, X } from 'lucide-react';
import { DESTINATIONS, IMPORT_ORDER } from './recordTypes';

const ACCEPTED_FILES = '.xlsx,.xls,.csv';

const stepLabel = ({ destination, itemType }) => {
  if (destination === 'catalog') return itemType === 'service' ? 'Services' : 'Products';
  return DESTINATIONS.find((d) => d.key === destination)?.label || destination;
};

const rowCount = (sheet) => Math.max(0, sheet.rows.length - 1);
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

export default function ImportUploadStep({
  destination,
  onDestinationChange,
  itemType,
  onItemTypeChange,
  fileName,
  reading,
  parseError,
  onFile,
  onClearFile,
  sheets,
  sheetName,
  onSheetChange,
  destinationForSheet,
  finished,
  onPickStep,
  draft,
  onResumeDraft,
  onDiscardDraft,
  onCancel,
  onContinue,
  canContinue,
}) {
  const inputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    e.target.value = '';
  };

  const destinationLabel = DESTINATIONS.find((d) => d.key === destination)?.label || '';
  const noun =
    destination === 'catalog'
      ? itemType === 'service'
        ? 'services'
        : 'products'
      : destinationLabel.toLowerCase();

  // An Ivy account export arrives as one sheet per section.
  const isIvy = sheets.some((sheet) => sheet.section);
  const importable = sheets.filter((sheet) => destinationForSheet(sheet.name));
  const others = sheets.filter((sheet) => !destinationForSheet(sheet.name));
  const plan = isIvy
    ? IMPORT_ORDER.map((step) => ({
        step,
        sheet: importable.find((sheet) => {
          const match = destinationForSheet(sheet.name);
          return (
            match.destination === step.destination &&
            (step.destination !== 'catalog' || match.itemType === step.itemType)
          );
        }),
      })).filter(({ sheet }) => sheet)
    : [];

  const sheetOption = (sheet) => (
    <option key={sheet.name} value={sheet.name}>
      {isIvy ? `${sheet.name} (${plural(rowCount(sheet), 'row', 'rows')})` : sheet.name}
    </option>
  );

  return (
    <>
      {draft && (
        <div className="pi-banner pi-banner--info pi-draft-banner" role="status">
          <Info size={18} aria-hidden="true" />
          <span>
            You have an unfinished import of <strong>{draft.fileName}</strong>, saved{' '}
            {new Date(draft.savedAt).toLocaleString()}.
          </span>
          <div className="pi-banner-actions">
            <button type="button" className="pi-btn pi-btn--ghost" onClick={onDiscardDraft}>
              Discard
            </button>
            <button type="button" className="pi-btn pi-btn--primary" onClick={onResumeDraft}>
              Resume
            </button>
          </div>
        </div>
      )}

      <section className="pi-card pi-upload-card">
        <div className="pi-upload-selects">
          <label className="pi-field">
            <span className="pi-field-label">Destination</span>
            <select
              id="pi-destination"
              value={destination}
              onChange={(e) => onDestinationChange(e.target.value)}
            >
              {DESTINATIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {destination === 'catalog' && (
            <label className="pi-field">
              <span className="pi-field-label">Product or Services</span>
              <select
                id="pi-item-type"
                value={itemType}
                onChange={(e) => onItemTypeChange(e.target.value)}
              >
                <option value="product">Products</option>
                <option value="service">Services</option>
              </select>
            </label>
          )}
        </div>

        <div
          className={`pi-dropzone${isDragActive ? ' is-active' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragActive(false);
          }}
          onDrop={handleDrop}
        >
          <FileSpreadsheet
            className="pi-excel-icon pi-excel-icon--large"
            size={52}
            aria-hidden="true"
          />
          <p className="pi-dropzone-title">Drag your Excel file here</p>
          <button
            type="button"
            className="pi-btn pi-btn--primary"
            onClick={() => inputRef.current?.click()}
          >
            Browse files
          </button>
          <span className="pi-dropzone-hint">
            .xlsx or .csv, including Ivy&apos;s account export
          </span>
          <input
            ref={inputRef}
            id="pi-file-input"
            type="file"
            accept={ACCEPTED_FILES}
            onChange={handlePick}
            hidden
          />
        </div>

        {(fileName || reading || parseError) && (
          <div className={`pi-file-row${parseError ? ' has-error' : ''}`}>
            <FileSpreadsheet className="pi-excel-icon" size={26} aria-hidden="true" />
            <span className="pi-file-name">{fileName || 'Reading file…'}</span>
            {reading && (
              <span className="pi-file-status">
                <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Reading file…
              </span>
            )}
            {!reading && parseError && (
              <span className="pi-file-status is-error">{parseError}</span>
            )}
            {!reading && !parseError && fileName && (
              <span className="pi-file-status is-ok">
                <CheckCircle2 size={18} aria-hidden="true" /> File uploaded
              </span>
            )}
            <button
              type="button"
              className="pi-icon-btn"
              onClick={onClearFile}
              aria-label="Remove file"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {sheets.length > 1 && (
          <label className="pi-field pi-sheet-field">
            <span className="pi-field-label">{isIvy ? 'Section' : 'Sheet'}</span>
            <select id="pi-sheet" value={sheetName} onChange={(e) => onSheetChange(e.target.value)}>
              {isIvy ? (
                <>
                  <optgroup label="Can be imported">{importable.map(sheetOption)}</optgroup>
                  {others.length > 0 && (
                    <optgroup label="Not imported by this tool">{others.map(sheetOption)}</optgroup>
                  )}
                </>
              ) : (
                sheets.map(sheetOption)
              )}
            </select>
          </label>
        )}

        {plan.length > 0 && (
          <div className="pi-plan" aria-labelledby="pi-plan-title">
            <p id="pi-plan-title" className="pi-plan-title">
              <strong>Ivy account export.</strong> Import these in order, so projects can link to
              clients and tax rates, rooms, proposals and time to projects, and products to vendors.
            </p>
            <ol className="pi-plan-list">
              {plan.map(({ step, sheet }, index) => {
                const done = finished[sheet.name] !== undefined;
                const current = sheet.name === sheetName;
                return (
                  <li key={sheet.name}>
                    <button
                      type="button"
                      className={`pi-plan-step${current ? ' is-current' : ''}${done ? ' is-done' : ''}`}
                      onClick={() => onPickStep(step, sheet.name)}
                      aria-current={current ? 'step' : undefined}
                    >
                      <span className="pi-plan-dot">
                        {done ? <Check size={14} strokeWidth={3} aria-hidden="true" /> : index + 1}
                      </span>
                      <span className="pi-plan-text">
                        <strong>{stepLabel(step)}</strong>
                        <span className="pi-muted">
                          {' '}
                          {/* Invoices also lists invoices and retainers, so its row count misleads. */}
                          {step.destination === 'proposals'
                            ? `from ${sheet.name} and Invoice Items`
                            : `from ${sheet.name} · ${plural(rowCount(sheet), 'row', 'rows')}`}
                        </span>
                      </span>
                      {done && (
                        <span className="pi-chip pi-chip--ok">{finished[sheet.name]} imported</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>
            {others.length > 0 && (
              <p className="pi-plan-note">
                Not imported: {others.map((sheet) => sheet.name).join(', ')}.
              </p>
            )}
          </div>
        )}
      </section>

      <div className="pi-footer">
        <p className="pi-footer-note">
          <Info size={16} aria-hidden="true" /> You can choose {noun} and correct their details
          before importing.
        </p>
        <div className="pi-footer-actions">
          <button type="button" className="pi-btn pi-btn--outline" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="pi-btn pi-btn--primary pi-btn--lg"
            onClick={onContinue}
            disabled={!canContinue}
          >
            Continue to Map Columns <ArrowRight size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
}
